import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useTranslation } from 'react-i18next'

import useSettings from '@/hooks/useSettings'
import KorektyService from '@/services/KorektyService'
import InvoicesService from '@/services/InvoicesService'

import useKorektaFormState from '../common/useKorektaFormState'
import NotesSection from '../common/NotesSection'
import DateDiscountTermSection from '../common/DateDiscountTermSection'
import CorrectionProductTable from '../common/CorrectionProductTable'
import TotalsSection from '../common/TotalsSection'
import {
	appendInfaktCustomerToPayload,
	buildInfaktCustomerFromInvoice,
	buildPayload,
} from '../common/submitKorekta'

const dec = v => {
	if (v == null) return 0
	if (typeof v === 'object' && v.$numberDecimal !== undefined) {
		const n = Number(v.$numberDecimal)
		return Number.isFinite(n) ? n : 0
	}
	const n = Number(v)
	return Number.isFinite(n) ? n : 0
}

const toYMD = d => {
	if (!d) return new Date().toISOString().slice(0, 10)
	const dt = new Date(d)
	if (Number.isNaN(dt.getTime())) return new Date().toISOString().slice(0, 10)
	return dt.toISOString().slice(0, 10)
}

const buildProductsFromInvoice = (invoice) =>
	(invoice?.products || []).map(p => {
		const before = {
			price: dec(p.price),
			count: dec(p.count) || 1,
			taxPercent: dec(p.taxPercent),
			subtotal: dec(p.subtotal),
			taxValue: dec(p.taxValue),
			total: dec(p.total),
		}
		return {
			name: p.name || '',
			sku: p.sku || '',
			article_number: p.article_number || '',
			productId: (p.productId?._id || p.productId || '').toString(),
			productName: p.productName || '',
			before: { ...before },
			after: { ...before },
		}
	})

export default function KorektaAdd() {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const invoiceId = searchParams.get('invoiceId')
	const { t } = useTranslation()
	const { settings } = useSettings()
	const currencySymbol = settings?.companyCurrencySymbol?.trim() || 'PLN'
	const fmtMoney = n => `${currencySymbol} ${Number(n ?? 0).toFixed(2)}`

	const { form, setField, setForm, setAfterField } = useKorektaFormState()
	const [error, setError] = useState(null)
	const [loading, setLoading] = useState(true)
	const [submitting, setSubmitting] = useState(false)
	const [sourceInvoice, setSourceInvoice] = useState(null)

	useEffect(() => {
		if (!invoiceId) {
			setLoading(false)
			setError(t('korekty.add.messages.requiresInvoice'))
			return
		}

		let alive = true
		;(async () => {
			setLoading(true)
			setError(null)
			try {
				const { data, error: loadError } = await InvoicesService.getInvoiceById(invoiceId)
				if (!alive) return
				if (loadError || !data) throw new Error(loadError || t('korekty.add.messages.invoiceLoadFailed'))

				const hasInfakt = Boolean(data.infaktInvoiceUuid || data.infakt_invoice_uuid)
				if (!hasInfakt) throw new Error(t('korekty.add.messages.requiresInfaktInvoice'))
				if (!data.number) throw new Error(t('korekty.add.messages.missingInvoiceNumber'))

				const customerId = (data.customerId?._id || data.customerId || '').toString()
				const customerName = (data.customerName || data.customerId?.nazwa || '').trim()
				const products = buildProductsFromInvoice(data)
				if (!products.length) throw new Error(t('korekty.add.messages.noProducts'))

				setSourceInvoice(data)
				setForm({
					customerId,
					customerName,
					invoiceDate: toYMD(new Date()),
					dueDate: toYMD(data.dueDate || new Date()),
					correctedInvoiceNumber: data.number,
					sourceInvoiceId: data._id,
					correctionReason: '',
					publicNotes: '',
					privateNotes: '',
					paymentType: data.paymentType || 'transfer',
					products,
					status: 'issued',
					companyId: data.companyId || '',
					companyName: data.companyName || '',
				})
			} catch (err) {
				if (alive) setError(err.message || t('korekty.add.messages.createFailed'))
			} finally {
				if (alive) setLoading(false)
			}
		})()

		return () => { alive = false }
	}, [invoiceId, setForm, t])

	const handleSave = useCallback(async e => {
		e?.preventDefault()
		if (submitting || !sourceInvoice) return
		setSubmitting(true)
		setError(null)

		try {
			const payload = buildPayload(form)
			appendInfaktCustomerToPayload(payload, buildInfaktCustomerFromInvoice(sourceInvoice))
			if (!payload.companyId && sourceInvoice.companyId) payload.companyId = sourceInvoice.companyId
			if (!payload.companyName && sourceInvoice.companyName) payload.companyName = sourceInvoice.companyName
			if (!payload.infakt_client_nip && sourceInvoice.infakt_client_nip) {
				payload.infakt_client_nip = sourceInvoice.infakt_client_nip
			}

			const { success, error: apiError, data } = await KorektyService.createKorekta(payload)
			if (!success) throw new Error(apiError || t('korekty.add.messages.createFailed'))

			navigate(`/ksiegowosc/korekty/details/${data._id}`)
		} catch (err) {
			setError(err.message || t('korekty.add.messages.createFailed'))
			await Swal.fire({
				icon: 'error',
				title: t('korekty.add.messages.errorTitle'),
				text: err.message || t('korekty.add.messages.createFailed'),
			})
		} finally {
			setSubmitting(false)
		}
	}, [form, navigate, sourceInvoice, submitting, t])

	if (loading) {
		return (
			<div className="min-h-[40vh] flex items-center justify-center text-gray-600">
				{t('korekty.edit.loading')}
			</div>
		)
	}

	if (!invoiceId || !sourceInvoice) {
		return (
			<div className="max-w-3xl mx-auto px-4 py-10">
				<div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-amber-900">
					<p className="font-medium">{error || t('korekty.add.messages.requiresInvoice')}</p>
					<p className="mt-2 text-sm">{t('korekty.add.messages.requiresInvoiceHint')}</p>
					<Link
						to="/ksiegowosc/invoices"
						className="mt-4 inline-flex rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">
						{t('korekty.add.messages.goToInvoices')}
					</Link>
				</div>
			</div>
		)
	}

	return (
		<div className="w-full px-4 sm:px-6 lg:px-8 py-6">
			<form onSubmit={handleSave} className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
				<div className="px-4 sm:px-6 py-6 border-b border-gray-200">
					<h2 className="text-2xl font-bold text-gray-900">{t('korekty.add.title')}</h2>
					<p className="text-sm text-gray-500 mt-1">
						{t('korekty.add.sourceInvoice')}:{' '}
						<Link
							to={`/ksiegowosc/invoices/details/${sourceInvoice._id}`}
							className="font-medium text-blue-600 hover:underline">
							{sourceInvoice.number}
						</Link>
						{form.companyName ? ` · ${form.companyName}` : ''}
					</p>
				</div>

				{error && (
					<div className="mx-4 sm:mx-6 mt-6 rounded-lg bg-red-50 p-4 border border-red-200 text-sm text-red-700">
						{error}
					</div>
				)}

				<div className="px-4 sm:px-6 py-6 space-y-6">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								{t('korekty.list.table.company')}
							</label>
							<input
								className="block w-full rounded-lg border-0 py-2.5 px-3.5 ring-1 ring-inset ring-gray-300 bg-gray-50"
								value={form.companyName || '—'}
								readOnly
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								{t('korekty.form.correctedInvoiceNumber')}
							</label>
							<input
								className="block w-full rounded-lg border-0 py-2.5 px-3.5 ring-1 ring-inset ring-gray-300 bg-gray-50"
								value={form.correctedInvoiceNumber || ''}
								readOnly
							/>
						</div>
					</div>

					<DateDiscountTermSection form={form} setField={setField} hideCorrectedNumber />

					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							{t('korekty.form.correctionReason')}
						</label>
						<select
							value={form.correctionReason || ''}
							onChange={e => setField('correctionReason', e.target.value)}
							className="block w-full rounded-lg border-0 py-2.5 px-3.5 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 text-sm"
							required>
							<option value="">{t('korekty.form.correctionReasonPlaceholder')}</option>
							<option value="accounting_error">{t('korekty.form.correctionReasons.accounting_error')}</option>
							<option value="other">{t('korekty.form.correctionReasons.other')}</option>
						</select>
					</div>

					<div>
						<h3 className="text-sm font-semibold text-gray-900 mb-2">{t('korekty.form.products.title')}</h3>
						<p className="text-xs text-gray-500 mb-3">{t('korekty.form.products.hint')}</p>
						<CorrectionProductTable
							products={form.products}
							setAfterField={setAfterField}
							fmtMoney={fmtMoney}
						/>
					</div>

					<NotesSection form={form} setField={setField} />
					<TotalsSection form={form} fmtMoney={fmtMoney} />
				</div>

				<div className="px-4 sm:px-6 py-6 border-t border-gray-200 flex gap-3">
					<button
						type="submit"
						disabled={submitting}
						className={`px-5 py-2 rounded text-white ${submitting ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
						{submitting ? t('korekty.add.submitting') : t('korekty.add.submit')}
					</button>
					<button
						type="button"
						onClick={() => navigate(`/ksiegowosc/invoices/details/${sourceInvoice._id}`)}
						className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
						{t('korekty.add.cancel')}
					</button>
				</div>
			</form>
		</div>
	)
}
